import { useMutation } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import styled from "@emotion/styled";
import { Link, Navbar, NavRight, NavTitle, Page, Popup as F7Popup, TextEditor as F7TextEditor } from "framework7-react";
import { useCallback, useState } from "react";

import { DailyDiaryDateTitle } from "@/components/DailyDiaryDateTitle";
import { formatISODateTimeString } from "@/config/date-fns";
import { createPage, PageComponentProps } from "@/factories/createPage";
import { getFragmentData } from "@/generated/graphql";
import {
  DailyDiaryFragment,
  DailyDiaryFragmentDoc,
  GetDailyDiaryDocument,
  UpsertDailyDiaryDocument,
} from "@/generated/graphql/graphql";
import { useBack } from "@/hooks/useBack";
import { usePopupPageReady } from "@/hooks/usePopupPageReady";
import { AppBridgeProvider } from "@/providers/AppBridgeProvider";

const Popup = styled(F7Popup)`
  --f7-popup-border-radius: 15px 15px 0 0;
  --f7-popup-tablet-border-radius: 15px;
  --f7-block-strong-border-color: transparent;
  height: calc(100% - var(--f7-navbar-height) - var(--f7-safe-area-top));
  top: calc(var(--f7-navbar-height) + var(--f7-safe-area-top));

  .page-content {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  .block-title {
    margin-left: 0.5rem;
  }
  .daily-diary-form {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding-bottom: calc(1rem + var(--f7-safe-area-bottom));
  }
  .text-editor {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
`;

const TextEditor = styled(F7TextEditor)`
  background-color: transparent;
  border: none;
  flex: 1;
  margin: 0;
`;

export const DailyDiaryPopupPage = createPage("DailyDiaryPopupPage", PageComponent);

function PageComponent({ pageName, f7route, ...routeProps }: PageComponentProps) {
  const { getPageProps, isPageReady, swipeHandler } = usePopupPageReady(pageName);

  const date = f7route.params.date as string;
  const { data } = useQuery(GetDailyDiaryDocument, {
    skip: !isPageReady,
    variables: { date: formatISODateTimeString(date) },
  });
  const dailyDiary = getFragmentData(DailyDiaryFragmentDoc, data?.dailyDiary);

  return (
    <AppBridgeProvider
      f7route={f7route}
      isVisible={isPageReady}
      {...routeProps}
    >
      <Popup
        data-component={pageName}
        {...getPageProps()}
      >
        <Page>
          {swipeHandler}
          {dailyDiary ? (
            <DailyDiaryForm dailyDiary={dailyDiary} />
          ) : (
            <div className="daily-diary-form">
              <Navbar>
                <NavTitle>
                  <DailyDiaryDateTitle dailyDiary={{ date: formatISODateTimeString(date) }} />
                </NavTitle>
                <NavRight>
                  <Link popupClose>完了</Link>
                </NavRight>
              </Navbar>
            </div>
          )}
        </Page>
      </Popup>
    </AppBridgeProvider>
  );
}

function DailyDiaryForm({ dailyDiary }: { dailyDiary: DailyDiaryFragment }) {
  const back = useBack();
  const [upsertDailyDiary] = useMutation(UpsertDailyDiaryDocument);
  const [body, setBody] = useState(dailyDiary.body);

  const save = useCallback(() => {
    void upsertDailyDiary({
      variables: {
        dailyDiaryInput: { body },
        date: dailyDiary.date,
      },
    })
      .then(() => {
        back();
      })
      .catch(console.error);
  }, [back, body, dailyDiary.date, upsertDailyDiary]);

  return (
    <div className="daily-diary-form">
      <Navbar>
        <NavTitle>
          <DailyDiaryDateTitle dailyDiary={dailyDiary} />
        </NavTitle>
        <NavRight>
          <Link onClick={save}>完了</Link>
        </NavRight>
      </Navbar>

      <TextEditor
        buttons={[]}
        mode="keyboard"
        placeholder="どんな一日でしたか？"
        value={body}
        onTextEditorChange={setBody}
      />
    </div>
  );
}
